/* eslint-disable react-hooks/exhaustive-deps */
import { ExclamationCircleOutlined, ShoppingOutlined, UserOutlined } from '@ant-design/icons';
import { App, Badge, Button } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import logo from '../../assets/img/icon.png';
import SearchInput from '../../components/ui/SearchInput';
import { clearOrderBroadcast, useOrderBroadcast } from '../../hooks/useOrderBroadcast';
import { getGuestCustomer } from '../../redux/actions/customers';
import { addOrder, addOrderItem, clearOrderItems, deleteOrder, getOrdersById, removeOrderItem } from '../../redux/actions/order';
import { getProductDetails } from '../../redux/actions/products';
import { getRoutes } from '../../routes/Routes';
import { calculateOrderSummary, getApplicableTierPrice } from '../../utils';
import { formatOrderItems } from '../../utils/formatOrderItems';
import { decodeId, encodeId } from '../../utils/idEncoder';
import { kioskErrorToast } from '../../utils/toast';
import GuestInfoModal from './GuestInfoModal';
import KioskCart from './KioskCart';
import KioskCustomerProfileModal from './KioskCustomerProfileModal';
import KioskSidebar, { categoryNames } from './KioskSidebar';
import KioskTimeoutModal from './KioskTimeoutModal';
import ProductGrid from './ProductGrid';
import { useKioskIdentity } from './useKioskIdentity';
import useKioskInactivity from './useKioskInactivity';

const IDLE_TIMEOUT_SECONDS = 60;
const WARNING_DURATION_SECONDS = 20;

const Kiosk = () => {
  const { modal: antdModal } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  useKioskIdentity();
  const customer = location.state?.customer;
  const customerNetsuiteId = customer?.customer_netsuite_id;
  const rawOrderId = new URLSearchParams(location.search).get('orderId');
  const orderId = rawOrderId ? decodeId(rawOrderId) : null;

  const [selectedCategories, setSelectedCategories] = useState([categoryNames[0]]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [order, setOrder] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isGuestModalForceOpen, setIsGuestModalForceOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isResettingSession, setIsResettingSession] = useState(false);
  const [isClearingCart, setIsClearingCart] = useState(false);
  const [recentlyRemovedProduct, setRecentlyRemovedProduct] = useState(null);
  const [guestInfo, setGuestInfo] = useState(() => {
    try {
      const saved = sessionStorage.getItem('kiosk_guest_info');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [guestNetsuiteId, setGuestNetsuiteId] = useState(null);
  const [isCustomerLoading, setIsCustomerLoading] = useState(!customerNetsuiteId);
  const [isDevIdleDisabled] = useState(() => {
    return localStorage.getItem('dev_idle_disabled') === 'true';
  });
  const [terminalReference, setTerminalReference] = useState(null);

  const activeCustomerNetsuiteId = customerNetsuiteId || guestNetsuiteId;

  useEffect(() => {
    if (guestInfo) {
      sessionStorage.setItem('kiosk_guest_info', JSON.stringify(guestInfo));
    } else {
      sessionStorage.removeItem('kiosk_guest_info');
    }
  }, [guestInfo]);

  const contentAreaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastUpdatedItemIdRef = useRef(null);
  const lastUpdatedTimeRef = useRef({});

  const { subtotal, vat: taxTotal, grandTotal } = calculateOrderSummary(cartItems);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const toggleCategory = (catName) => {
    setProductSearchTerm('');

    if (
      catName.trim().toLowerCase() === 'all product' ||
      catName.trim().toLowerCase() === 'all products'
    ) {
      setSelectedCategories([categoryNames[0]]);
      return;
    }

    setSelectedCategories((prev) => {
      const isAlreadySelected = prev.some(
        (c) => c.toLowerCase() === catName.toLowerCase()
      );

      if (isAlreadySelected) {
        return [categoryNames[0]];
      } else {
        return [catName];
      }
    });
  };

  const resetToDefaultCategory = useCallback(() => {
    setSelectedCategories([categoryNames[0]]);
  }, []);

  const categoriesList = Array.isArray(selectedCategories)
    ? selectedCategories
    : [selectedCategories || 'All Products'];

  useEffect(() => {
    const fetchGuestId = async () => {
      try {
        const res = await getGuestCustomer();
        if (res?.ack) {
          const id =
            res?.customer?.customer_netsuite_id ||
            res?.customer?.netsuite_id ||
            res?.customer_netsuite_id ||
            res?.netsuite_id ||
            res?.id;
          if (id) {
            setGuestNetsuiteId(res?.customer?.customer_netsuite_id || res?.customer?.netsuite_id);
          }
        }
      } finally {
        setIsCustomerLoading(false);
      }
    };
    if (!customerNetsuiteId && !guestNetsuiteId) {
      setIsCustomerLoading(true);
      fetchGuestId();
    } else {
      setIsCustomerLoading(false);
    }
  }, [customerNetsuiteId]);

  useEffect(() => {
    if (orderId) {
      getOrderItems();
    }
  }, [orderId]);

  useEffect(() => {
    if (location.state?.openCart) {
      setIsCartOpen(true);
      navigate(
        { pathname: location.pathname, search: location.search },
        { state: { ...location.state, openCart: undefined }, replace: true }
      );
    }
  }, [location.state?.openCart, navigate, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      contentAreaRef.current.scrollTop = 0;
    }
  }, [selectedCategories, productSearchTerm]);

  useOrderBroadcast({
    enabled: !isResettingSession,
    customer: isResettingSession
      ? null
      : {
        name: customer?.title || guestInfo?.name,
        phone: customer?.phone || guestInfo?.phone,
        email: customer?.invoice_email || guestInfo?.email,
      },
    orderDate: null,
    orderItems: cartItems,
    totals: {
      subtotal,
      vat: taxTotal,
      grandTotal,
    },
  });

  const getOrderItems = useCallback(
    async (targetOrderId) => {
      const id = targetOrderId || orderId;
      if (!id) return;
      const res = await getOrdersById(id, { toast: false });
      setOrder(res?.order || null);
      const formattedItems = formatOrderItems(res?.order_details || []);
      const sortedOrderItems = orderBy(formattedItems, ['id'], ['asc']);
      setCartItems(sortedOrderItems);
      return sortedOrderItems;
    },
    [orderId]
  );

  const syncCartToBackend = useCallback(
    async (item, targetQty) => {
      if (!orderId || !item || !targetQty) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const pId = item?.product_netsuite_id || item?.netsuite_id || item?.id;
      const requestStartTime = Date.now();

      let tiers = item?.tiers;
      if (!tiers?.length) {
        const details = await getProductDetails({
          id: item?.product_netsuite_id || item?.netsuite_id,
          details: 1,
          loading: false,
        });
        tiers = details?.product || [];

      }

      const dynamicPrice = getApplicableTierPrice({
        ...item,
        tiers,
        uom: item?.uom || 'Case',
        product_case_quantity: item?.case_quantity || item?.product_case_quantity || 1,
        quantity: targetQty,
        price_level: item?.price_level,
      });

      const body = {
        id: item?.id,
        order_id: orderId,
        product_netsuite_id: item?.product_netsuite_id || item?.netsuite_id,
        quantity: targetQty,
        unit_price: dynamicPrice?.tier_price || dynamicPrice?.unit_price || item?.unit_price,
        price_level: item?.price_level,
        uom: item?.uom || 'Case',
      };

      try {
        const response = await addOrderItem(body, null, false, {
          toast: false,
          signal: controller.signal,
        });

        if (response === 'ERR_CANCELED' || controller.signal.aborted) {
          return;
        }

        if (response?.ack) {
          if (!lastUpdatedTimeRef.current[pId] || lastUpdatedTimeRef.current[pId] <= requestStartTime) {
            await getOrderItems(orderId);
          }
        } else {
          kioskErrorToast("Update Failed", response?.message || "Unable to update item quantity.");
        }
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || controller.signal.aborted) {
          return;
        }
        console.error('Failed to update cart item quantity:', err);
      }
    },
    [orderId, getOrderItems]
  );

  const debouncedSyncCart = useDebouncedCallback(syncCartToBackend, 400);


  useEffect(() => {
    const handleBeforeUnload = () => {
      clearOrderBroadcast();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      debouncedSyncCart.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      clearOrderBroadcast();
    };
  }, [debouncedSyncCart]);

  const resetSession = useCallback(
    async ({ orderAlreadyDeleted = false } = {}) => {
      setIsResettingSession(true);
      debouncedSyncCart.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      clearOrderBroadcast();
      if (orderId && !orderAlreadyDeleted) {
        try {
          await deleteOrder(orderId, { toast: false });
        } catch (err) {
          console.error('Error deleting order on session reset:', err);
        }
      }
      setCartItems([]);
      setRecentlyRemovedProduct(null);
      setGuestInfo(null);
      setProductSearchTerm('');
      setIsCartOpen(false);
      setIsCheckingOut(false);
      setIsPaymentProcessing(false);
      setIsSidebarOpen(false);
      resetToDefaultCategory();
      clearOrderBroadcast();
      if (customer) {
        navigate({ pathname: getRoutes().account_kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
      } else {
        navigate({ pathname: getRoutes().kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
        window.navigation?.reload?.();
      }
    },
    [orderId, resetToDefaultCategory, navigate, debouncedSyncCart, customer]
  );

  const showCancelConfirm = () => {
    antdModal.confirm({
      title: 'Cancel Order?',
      icon: <ExclamationCircleOutlined style={{ color: '#ef4444' }} />,
      content: 'Are you sure you want to cancel the current session? All items will be removed and your session will restart.',
      okText: 'Yes, Cancel',
      cancelText: 'Keep Shopping',
      centered: true,
      zIndex: 2500,
      maskClosable: false,
      okButtonProps: {
        danger: true,
        size: 'large',
      },
      cancelButtonProps: {
        size: 'large',
      },
      onOk: async () => {
        await resetSession();
      },
    });
  };

  const {
    isWarningOpen,
    remainingSeconds,
    resetTimer,
    triggerTimeout,
  } = useKioskInactivity({
    idleTimeout: IDLE_TIMEOUT_SECONDS,
    warningDuration: WARNING_DURATION_SECONDS,
    enabled: !!orderId && !isPaymentProcessing && !isDevIdleDisabled,
    onTimeout: resetSession,
  });

  const handelAddToCart = useCallback(
    async ({ product, orderId: targetOrderId, qty = 1 } = {}) => {
      debouncedSyncCart.flush();
      const activeOrderId = targetOrderId || orderId;
      if (!activeOrderId) return;

      const quantityToAdd =
        typeof qty === 'number' && qty > 0 ? Math.min(999, qty) : 1;

      let unitPrice = product?.unit_price;
      let tiers = product?.tiers;

      if (!tiers?.length) {
        const details = await getProductDetails({
          id: product?.netsuite_id || product?.product_netsuite_id,
          details: 1,
          loading: false,
        });
        tiers = details?.product || [];
      }

      if (tiers?.length) {
        const dynamicPrice = getApplicableTierPrice({
          ...product,
          tiers,
          uom: product?.uom || 'Case',
          product_case_quantity: product?.case_quantity || product?.product_case_quantity || 1,
          quantity: quantityToAdd,
          price_level: product?.price_level,
        });
        if (dynamicPrice?.tier_price || dynamicPrice?.unit_price) {
          unitPrice = dynamicPrice?.tier_price || dynamicPrice?.unit_price;
        }
      }

      const body = {
        order_id: activeOrderId,
        product_netsuite_id: product?.netsuite_id || product?.product_netsuite_id,
        quantity: quantityToAdd,
        unit_price: unitPrice,
        price_level: product?.price_level,
        uom: product?.uom || 'Case',
      };

      const productPayload = {
        ...product,
        unit_price: unitPrice,
        tiers,
      };

      const response = await addOrderItem(body, productPayload, false, { toast: false });

      if (response?.ack) {
        await getOrderItems(activeOrderId);
      } else {
        kioskErrorToast("Add Failed", response?.message || "Unable to add item to cart.");
      }

      return response;
    },
    [orderId, getOrderItems, debouncedSyncCart]
  );

  const getOrCreateOrder = useCallback(async () => {
    if (orderId) return orderId;

    const res = await addOrder(
      {
        customer_netsuite_id: activeCustomerNetsuiteId,
        type: 1,
        order_date: dayjs().format('YYYY-MM-DD'),
        is_frontdesk: 0,
        is_kiosk: 1,
      },
      { toast: false }
    );

    const newOrderId = res?.record_id || res?.order_id;

    if (!newOrderId) return null;

    navigate(
      {
        pathname: getRoutes().kiosk.route(),
        search: `?${new URLSearchParams({
          orderId: encodeId(String(newOrderId)),
        })}`,
      },
      {
        state: {
          customer,
        },
        replace: true,
      }
    );

    return newOrderId;
  }, [orderId, activeCustomerNetsuiteId, navigate, customer]);

  const addToCart = useCallback(
    async (product, qty = 1) => {
      if (cartItems.length === 0) {
        setIsCartOpen(true);
      }

      if (!orderId) {
        const newOrderId = await getOrCreateOrder();
        if (!newOrderId) return;

        return await handelAddToCart({
          product,
          orderId: newOrderId,
          qty,
        });
      }

      return handelAddToCart({
        product,
        orderId,
        qty,
      });
    },
    [orderId, getOrCreateOrder, handelAddToCart, cartItems.length]
  );

  const updateQuantity = useCallback(
    async (productId, newQuantity, itemOverride, newUom) => {
      const isMatch = (item) =>
        item.id === productId ||
        item.netsuite_id === productId ||
        item.product_netsuite_id === productId;

      const currentItem = itemOverride || cartItems.find(isMatch);

      if (newQuantity <= 0 && newQuantity !== '') {
        debouncedSyncCart.cancel();
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        let removedItem;
        setCartItems((prev) => {
          removedItem = prev.find(isMatch);
          return prev.filter((item) => !isMatch(item));
        });

        const targetProduct = currentItem || removedItem;
        if (targetProduct) {
          setRecentlyRemovedProduct({
            product: targetProduct,
            timestamp: Date.now(),
          });
        }

        const orderItemId = currentItem?.id || productId;
        const res = await removeOrderItem(orderItemId, orderId, null, {
          toast: false,
          isGet: false,
        });

        if (!res?.ack) {
          setCartItems((prev) => [...prev, removedItem]);
        }
      } else {
        const clampedQty =
          newQuantity === '' ? '' : Math.min(999, Math.max(1, Number(newQuantity)));

        const targetQty = clampedQty === '' ? 1 : clampedQty;
        let nextUnitPrice = currentItem?.unit_price;
        const targetUom = newUom || currentItem?.uom || 'Case';

        if (currentItem?.tiers?.length) {
          const dynamicPrice = getApplicableTierPrice({
            ...currentItem,
            tiers: currentItem.tiers,
            uom: targetUom,
            product_case_quantity: currentItem?.case_quantity || currentItem?.product_case_quantity || 1,
            quantity: targetQty,
            price_level: currentItem?.price_level,
          });
          if (dynamicPrice?.tier_price || dynamicPrice?.unit_price) {
            nextUnitPrice = dynamicPrice?.tier_price || dynamicPrice?.unit_price;
          }
        }

        setCartItems((prev) =>
          prev.map((item) =>
            isMatch(item) ? { ...item, quantity: clampedQty, unit_price: nextUnitPrice, uom: targetUom } : item
          )
        );

        if (newQuantity !== '' && currentItem) {
          const pId = currentItem.product_netsuite_id || currentItem.netsuite_id || currentItem.id;
          lastUpdatedTimeRef.current[pId] = Date.now();

          if (
            lastUpdatedItemIdRef.current &&
            lastUpdatedItemIdRef.current !== productId
          ) {
            debouncedSyncCart.flush();
          }
          lastUpdatedItemIdRef.current = productId;
          debouncedSyncCart({ ...currentItem, uom: targetUom }, clampedQty);
        }
      }
    },
    [orderId, cartItems, debouncedSyncCart]
  );

  const clearCart = useCallback(async () => {
    debouncedSyncCart.cancel();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!orderId) {
      setCartItems([]);
      setRecentlyRemovedProduct(null);
      return { ack: true };
    }
    setIsClearingCart(true);
    try {
      const res = await clearOrderItems({ order_id: orderId }, { toast: false });
      if (res?.ack) {
        setCartItems([]);
        setRecentlyRemovedProduct(null);
      }
      return res;
    } finally {
      setIsClearingCart(false);
    }
  }, [orderId, customer, navigate, debouncedSyncCart]);

  return (
    <div className={clsx('kiosk-container', { 'cart-open': isCartOpen })}>
      {/* Categories Sidebar */}
      <KioskSidebar
        selectedCategories={categoriesList}
        toggleCategory={toggleCategory}
        isSidebarOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {isSidebarOpen && (
        <div
          className="kiosk-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Section */}
      <div className="kiosk-main-section">
        {/* Header */}
        <div className="kiosk-header">
          <div className="kiosk-branding">
            <img src={logo} alt="Magna Logo" className="kiosk-logo" />
            <div className="kiosk-search">
              <SearchInput
                placeholder="Search products..."
                className="kiosk-search-input w-full"
                value={productSearchTerm}
                onSearch={(val) => setProductSearchTerm(val)}
                delay={300}
                size="middle"
              />
            </div>
            <button
              type="button"
              onClick={toggleSidebar}
              className="kiosk-categories-btn"
            >
              <span>Categories</span>
              {categoriesList.length > 0 &&
                !categoriesList.some(
                  (c) =>
                    c.trim().toLowerCase() === 'all product' ||
                    c.trim().toLowerCase() === 'all products'
                ) && (
                  <span className="kiosk-categories-badge">
                    {categoriesList.length}
                  </span>
                )}
            </button>
          </div>

          <div className="kiosk-actions">
            <Button
              className="kiosk-cancel-order-btn font-bold"
              type="primary"
              danger
              size='large'
              onClick={showCancelConfirm}
            >
              Cancel Order
            </Button>

            <div
              className={clsx("kiosk-customer-info", "clickable")}
              onClick={() => {
                if (!customer) {
                  setIsGuestModalForceOpen(true);
                } else {
                  setIsProfileModalOpen(true);
                }
              }}
              style={{ cursor: 'pointer' }}
              title={!customer ? "Edit Guest Information" : "View Customer Profile"}
            >
              <UserOutlined className="kiosk-customer-icon" />
              <span className="kiosk-customer-name">
                {customer?.title || customer?.company_name || guestInfo?.name || 'Guest'}
              </span>
            </div>

            <div className="kiosk-cart-toggle">
              <Badge
                count={cartItems.length}
                showZero={false}
                color="#111827"
                offset={[-5, 5]}
              >
                <Button
                  type="primary"
                  shape="round"
                  icon={<ShoppingOutlined />}
                  onClick={() => setIsCartOpen(!isCartOpen)}
                  className="kiosk-cart-toggle-btn"
                >
                  {isCartOpen ? 'Hide Cart' : 'View Cart'}
                </Button>
              </Badge>
            </div>
          </div>
        </div>

        {/* Product Grid Area */}
        <div className="kiosk-content-area" ref={contentAreaRef}>
          <ProductGrid
            customerId={activeCustomerNetsuiteId}
            isCustomerLoading={isCustomerLoading}
            selectedCategories={categoriesList}
            activeCategory={categoriesList}
            searchTerm={productSearchTerm}
            addToCart={addToCart}
            getOrCreateOrder={getOrCreateOrder}
            cartItems={cartItems}
            recentlyRemovedProduct={recentlyRemovedProduct}
            updateQuantity={updateQuantity}
            disableScanning={isCheckingOut || isPaymentProcessing}
          />
        </div>
      </div>

      {/* Docked Cart */}
      {isCartOpen && (
        <div className="kiosk-cart-dock">
          <KioskCart
            customer={customer}
            order={order}
            cartItems={cartItems}
            guestInfo={guestInfo}
            updateQuantity={updateQuantity}
            onClose={() => setIsCartOpen(false)}
            clearCart={clearCart}
            isClearingCart={isClearingCart}
            onCheckoutStateChange={setIsCheckingOut}
            onPaymentProcessingChange={setIsPaymentProcessing}
            onTerminalReferenceChange={setTerminalReference}
          />
        </div>
      )}

      {/* Inactivity Warning Modal */}
      <KioskTimeoutModal
        isOpen={isWarningOpen}
        remainingSeconds={remainingSeconds}
        warningDuration={WARNING_DURATION_SECONDS}
        onContinue={resetTimer}
        onReset={triggerTimeout}
        cartItems={cartItems}
        orderId={orderId}
        terminalReference={terminalReference}
      />

      {/* Guest Info Modal */}
      <GuestInfoModal
        isOpen={(!customer && guestInfo === null) || isGuestModalForceOpen}
        initialValues={guestInfo}
        onSubmit={(info) => {
          setGuestInfo(info);
          setIsGuestModalForceOpen(false);
        }}
        onCancel={() => {
          if (isGuestModalForceOpen) {
            setIsGuestModalForceOpen(false);
          } else {
            setGuestInfo({});
          }
        }}
      />

      {/* Account Customer Profile Modal */}
      <KioskCustomerProfileModal
        isOpen={isProfileModalOpen}
        onCancel={() => setIsProfileModalOpen(false)}
        customer={customer}
      />
    </div>
  );
};

export default Kiosk;
